import { useState, useCallback, useRef, useMemo } from "react"
import { router, usePage } from "@inertiajs/react"
import { Button } from "@/components/ui/button"
import { Dialog, DialogContent, DialogBody, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table"
import { Label } from "@/components/ui/label"
import { Upload, FileSpreadsheet, ArrowLeft, ArrowRight, AlertCircle, Check, MapPin, Map, Building2, HelpCircle } from "lucide-react"
import { cn } from "@/lib/utils"
import { read, utils } from "xlsx"
import type { SharedProps, PipeStatusRecord, ProjectStatus } from "@/types"

type Props = {
  open: boolean
  onOpenChange: (open: boolean) => void
  pipeStatuses: PipeStatusRecord[]
  currentStatus: ProjectStatus
  onImportStarted?: (importId: string) => void
}

type Step = "upload" | "mapping" | "preview"

type FieldDef = {
  key: string
  label: string
  alternateMatches: string[]
}

const FIELDS: FieldDef[] = [
  { key: "project_id", label: "ID Regroupement", alternateMatches: ["id", "identifiant", "projectid", "project_id", "ref", "reference"] },
  { key: "project_name", label: "Nom du projet", alternateMatches: ["nom", "name", "project_name", "projectname", "nom projet", "nom du projet"] },
  { key: "prefix", label: "Préfixe", alternateMatches: ["prefix", "prefixe"] },
  { key: "address", label: "Adresse", alternateMatches: ["adresse", "address", "rue"] },
  { key: "zipcode", label: "Code postal", alternateMatches: ["code postal", "cp", "zipcode", "zip_code", "code_postal"] },
  { key: "commune", label: "Commune", alternateMatches: ["ville", "city", "commune", "city_name", "cityname"] },
  { key: "code_insee", label: "Code INSEE", alternateMatches: ["insee", "code_insee", "codeinsee"] },
  { key: "code_arr", label: "Code arrondissement", alternateMatches: ["arrondissement", "code_arr", "codearr"] },
  { key: "com_abs", label: "Commune absorbée", alternateMatches: ["com_abs", "comabs", "commune absorbee"] },
  { key: "parcel_section", label: "Section", alternateMatches: ["section", "parcel_section", "parcelsection"] },
  { key: "parcel_number", label: "Numéro de parcelle", alternateMatches: ["numero", "number", "parcel_number", "parcelnumber", "numero parcelle"] },
  { key: "parcel_idu", label: "IDU parcelle", alternateMatches: ["idu", "parcel_idu", "parcelidu"] },
  { key: "owner_gender", label: "Civilité propriétaire", alternateMatches: ["civilite", "civilité", "gender", "owner_gender"] },
  { key: "owner_last_name", label: "Nom propriétaire", alternateMatches: ["nom proprietaire", "nom propriétaire", "owner_last_name", "ownerlastname"] },
  { key: "owner_first_name", label: "Prénom propriétaire", alternateMatches: ["prenom proprietaire", "prénom propriétaire", "owner_first_name", "ownerfirstname"] },
  { key: "owner_address", label: "Adresse propriétaire", alternateMatches: ["adresse proprietaire", "adresse propriétaire", "owner_address"] },
  { key: "owner_zip_code", label: "CP propriétaire", alternateMatches: ["cp proprietaire", "cp propriétaire", "owner_zip_code"] },
  { key: "owner_city_name", label: "Ville propriétaire", alternateMatches: ["ville proprietaire", "ville propriétaire", "owner_city_name"] },
  { key: "owner_phone", label: "Tél. propriétaire", alternateMatches: ["telephone", "téléphone", "tel", "phone", "owner_phone"] },
  { key: "siren", label: "SIREN", alternateMatches: ["siren", "siret"] },
  { key: "notes", label: "Notes", alternateMatches: ["notes", "commentaire", "commentaires", "comment"] },
]

function normalizeString(s: string): string {
  return s
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-z0-9]/g, "")
}

function autoMatchColumn(header: string): string | null {
  const normalized = normalizeString(header)

  for (const field of FIELDS) {
    if (normalizeString(field.key) === normalized) return field.key
    if (normalizeString(field.label) === normalized) return field.key
    for (const alt of field.alternateMatches) {
      if (normalizeString(alt) === normalized) return field.key
    }
  }
  return null
}

type ResolutionLevel = "full" | "address" | "commune_only" | "none"

function getRowResolutionLevel(row: Record<string, string>): ResolutionLevel {
  const hasParcelIdu = !!row.parcel_idu?.trim()
  const hasParcelParts = !!row.code_insee?.trim() && !!row.parcel_section?.trim() && !!row.parcel_number?.trim()
  if (hasParcelIdu || hasParcelParts) return "full"

  const hasAddress = !!row.address?.trim() && !!row.zipcode?.trim()
  if (hasAddress) return "address"

  const hasCommune = !!row.commune?.trim() || !!row.code_insee?.trim()
  if (hasCommune) return "commune_only"

  return "none"
}

const RESOLUTION_CONFIG: Record<ResolutionLevel, { label: string; description: string; color: string; icon: typeof MapPin }> = {
  full: { label: "Parcelle complète", description: "Parcelle, commune, coordonnées, géométrie, surface", color: "bg-emerald-100 text-emerald-800", icon: Map },
  address: { label: "Adresse", description: "Coordonnées, commune", color: "bg-blue-100 text-blue-800", icon: MapPin },
  commune_only: { label: "Commune seule", description: "Commune uniquement", color: "bg-amber-100 text-amber-800", icon: Building2 },
  none: { label: "Aucune localisation", description: "Aucune donnée de localisation", color: "bg-red-100 text-red-800", icon: HelpCircle },
}

export function ProjectImportDialog({ open, onOpenChange, pipeStatuses, currentStatus, onImportStarted }: Props) {
  const { current_user } = usePage<SharedProps>().props
  const fileInputRef = useRef<HTMLInputElement>(null)

  const [step, setStep] = useState<Step>("upload")
  const [fileName, setFileName] = useState("")
  const [fileHeaders, setFileHeaders] = useState<string[]>([])
  const [rawRows, setRawRows] = useState<Record<string, string>[]>([])
  const [mapping, setMapping] = useState<Record<string, string>>({})
  const statusPipeStatuses = pipeStatuses.filter((ps) => ps.parent_status === currentStatus)
  const [pipeStatusId, setPipeStatusId] = useState(statusPipeStatuses[0]?.id ?? "")
  const [submitting, setSubmitting] = useState(false)
  const [dragOver, setDragOver] = useState(false)

  const reset = useCallback(() => {
    setStep("upload")
    setFileName("")
    setFileHeaders([])
    setRawRows([])
    setMapping({})
    setPipeStatusId("")
    setSubmitting(false)
    setDragOver(false)
  }, [])

  const handleClose = (open: boolean) => {
    if (!open) reset()
    onOpenChange(open)
  }

  const parseFile = useCallback((file: File) => {
    const reader = new FileReader()
    reader.onload = (e) => {
      const data = new Uint8Array(e.target?.result as ArrayBuffer)
      const workbook = read(data, { type: "array" })
      const firstSheet = workbook.Sheets[workbook.SheetNames[0]]
      const json = utils.sheet_to_json<Record<string, string>>(firstSheet, { raw: false, defval: "" })

      if (json.length === 0) return

      const headers = Object.keys(json[0])
      setFileHeaders(headers)
      setRawRows(json)
      setFileName(file.name)

      // Auto-match columns
      const autoMapping: Record<string, string> = {}
      for (const header of headers) {
        const match = autoMatchColumn(header)
        if (match && !Object.values(autoMapping).includes(match)) {
          autoMapping[header] = match
        }
      }
      setMapping(autoMapping)
      setStep("mapping")
    }
    reader.readAsArrayBuffer(file)
  }, [])

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (file) parseFile(file)
  }

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault()
    setDragOver(false)
    const file = e.dataTransfer.files?.[0]
    if (file) parseFile(file)
  }

  const mappedRows = rawRows.map((row) => {
    const mapped: Record<string, string> = {}
    for (const [header, fieldKey] of Object.entries(mapping)) {
      if (fieldKey) mapped[fieldKey] = row[header] || ""
    }
    return mapped
  })

  const resolutionCounts = useMemo(() => {
    const counts: Record<ResolutionLevel, number> = { full: 0, address: 0, commune_only: 0, none: 0 }
    for (const row of mappedRows) {
      counts[getRowResolutionLevel(row)]++
    }
    return counts
  }, [mappedRows])

  const validationErrors = 0

  const handleSubmit = () => {
    setSubmitting(true)
    router.post(
      "/projects/import",
      {
        organisation_id: current_user.organisation_id,
        pipe_status_id: pipeStatusId || undefined,
        rows: mappedRows,
      },
      {
        onSuccess: () => {
          handleClose(false)
        },
        onError: () => {
          setSubmitting(false)
        },
      },
    )
  }

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-5xl max-h-[90vh] flex flex-col">
        <DialogHeader>
          <DialogTitle>Importer des projets</DialogTitle>
        </DialogHeader>

        <DialogBody className="flex-1 overflow-y-auto">
          {step === "upload" && (
            <div
              className={cn(
                "flex flex-col items-center justify-center gap-4 rounded-lg border-2 border-dashed p-12 transition-colors",
                dragOver ? "border-primary bg-primary/5" : "border-muted-foreground/25",
              )}
              onDragOver={(e) => {
                e.preventDefault()
                setDragOver(true)
              }}
              onDragLeave={() => setDragOver(false)}
              onDrop={handleDrop}
            >
              <Upload className="text-muted-foreground size-10" />
              <div className="text-center">
                <p className="font-medium">Glissez-déposez votre fichier ici</p>
                <p className="text-muted-foreground text-sm">ou cliquez pour sélectionner un fichier CSV ou Excel</p>
              </div>
              <Button variant="outline" onClick={() => fileInputRef.current?.click()}>
                <FileSpreadsheet className="size-4" />
                Choisir un fichier
              </Button>
              <input ref={fileInputRef} type="file" accept=".csv,.xlsx,.xls" className="hidden" onChange={handleFileChange} />
            </div>
          )}

          {step === "mapping" && (
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <p className="text-sm">
                  <span className="font-medium">{fileName}</span> — {rawRows.length} lignes, {fileHeaders.length} colonnes
                </p>
                <p className="text-muted-foreground text-sm">
                  {Object.keys(mapping).length}/{fileHeaders.length} associée{Object.keys(mapping).length > 1 ? "s" : ""}
                </p>
              </div>

              <div className="overflow-x-auto rounded-md border">
                <table className="w-max min-w-full border-collapse">
                  <thead>
                    <tr className="bg-muted/50">
                      {fileHeaders.map((header) => (
                        <th key={header} className="border-r px-3 py-2 text-left text-xs font-semibold last:border-r-0" style={{ minWidth: 160 }}>
                          {header}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {rawRows.slice(0, 3).map((row, i) => (
                      <tr key={i} className="border-t">
                        {fileHeaders.map((header) => (
                          <td key={header} className="text-muted-foreground border-r px-3 py-1.5 text-xs last:border-r-0">
                            <span className="block max-w-40 truncate">{row[header] || ""}</span>
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                  <tfoot>
                    <tr className="border-t bg-muted/30">
                      {fileHeaders.map((header) => (
                        <td key={header} className="border-r px-2 py-2 last:border-r-0">
                          <Select
                            value={mapping[header] || "__none__"}
                            onValueChange={(fieldKey) => {
                              setMapping((prev) => {
                                const next = { ...prev }
                                if (fieldKey !== "__none__") {
                                  for (const [k, v] of Object.entries(next)) {
                                    if (v === fieldKey) delete next[k]
                                  }
                                }
                                if (fieldKey === "__none__") {
                                  delete next[header]
                                } else {
                                  next[header] = fieldKey
                                }
                                return next
                              })
                            }}
                          >
                            <SelectTrigger className={cn("h-7 text-xs", mapping[header] ? "border-primary" : "")}>
                              <SelectValue placeholder="— Ignorer —" />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="__none__">— Ignorer —</SelectItem>
                              {FIELDS.map((f) => {
                                const alreadyUsed = Object.entries(mapping).some(([k, v]) => v === f.key && k !== header)
                                return (
                                  <SelectItem key={f.key} value={f.key} disabled={alreadyUsed}>
                                    {f.label}{alreadyUsed ? " ✓" : ""}
                                  </SelectItem>
                                )
                              })}
                            </SelectContent>
                          </Select>
                        </td>
                      ))}
                    </tr>
                  </tfoot>
                </table>
              </div>
            </div>
          )}

          {step === "preview" && (
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <p className="text-sm">
                  <span className="font-medium">{mappedRows.length}</span> lignes à importer
                  {validationErrors > 0 && (
                    <span className="ml-2 text-destructive">
                      <AlertCircle className="mr-1 inline size-3.5" />
                      {validationErrors} ligne{validationErrors > 1 ? "s" : ""} sans nom de projet ni identifiant
                    </span>
                  )}
                </p>
              </div>

              <div className="flex flex-wrap gap-2">
                {(Object.entries(RESOLUTION_CONFIG) as [ResolutionLevel, typeof RESOLUTION_CONFIG.full][]).map(([level, config]) => {
                  const count = resolutionCounts[level]
                  if (count === 0) return null
                  const Icon = config.icon
                  return (
                    <div
                      key={level}
                      className={cn("inline-flex items-center gap-1.5 rounded-md px-2.5 py-1 text-xs font-medium", config.color)}
                      title={config.description}
                    >
                      <Icon className="size-3.5" />
                      {count} {config.label.toLowerCase()}
                    </div>
                  )
                })}
              </div>

              {statusPipeStatuses.length > 0 && (
                <div className="flex items-center gap-3">
                  <Label className="text-sm whitespace-nowrap">Sous-statut initial :</Label>
                  <Select value={pipeStatusId} onValueChange={setPipeStatusId}>
                    <SelectTrigger className="h-8 w-64">
                      <SelectValue placeholder="Par défaut" />
                    </SelectTrigger>
                    <SelectContent>
                      {statusPipeStatuses.map((ps) => (
                        <SelectItem key={ps.id} value={ps.id}>
                          {ps.label || ps.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}

              <div className="overflow-auto rounded-md border" style={{ maxHeight: "400px" }}>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-10">#</TableHead>
                      {FIELDS.filter((f) => Object.values(mapping).includes(f.key)).map((f) => (
                        <TableHead key={f.key} className="whitespace-nowrap text-xs">
                          {f.label}
                        </TableHead>
                      ))}
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {mappedRows.slice(0, 20).map((row, i) => {
                      const hasError = !row.project_name && !row.project_id
                      return (
                        <TableRow key={i} className={cn(hasError && "bg-destructive/5")}>
                          <TableCell className="text-muted-foreground text-xs">{i + 1}</TableCell>
                          {FIELDS.filter((f) => Object.values(mapping).includes(f.key)).map((f) => (
                            <TableCell key={f.key} className="max-w-40 truncate text-xs">
                              {row[f.key] || ""}
                            </TableCell>
                          ))}
                        </TableRow>
                      )
                    })}
                  </TableBody>
                </Table>
              </div>

              {mappedRows.length > 20 && (
                <p className="text-muted-foreground text-center text-xs">
                  Affichage des 20 premières lignes sur {mappedRows.length}
                </p>
              )}
            </div>
          )}
        </DialogBody>

        <DialogFooter className="flex items-center justify-between gap-2">
          <div>
            {step !== "upload" && (
              <Button
                variant="outline"
                onClick={() => setStep(step === "preview" ? "mapping" : "upload")}
              >
                <ArrowLeft className="size-4" />
                Retour
              </Button>
            )}
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => handleClose(false)}>
              Annuler
            </Button>
            {step === "mapping" && (
              <Button
                onClick={() => setStep("preview")}
                disabled={Object.values(mapping).length === 0}
              >
                Suivant
                <ArrowRight className="size-4" />
              </Button>
            )}
            {step === "preview" && (
              <Button onClick={handleSubmit} disabled={submitting || mappedRows.length === 0 || !pipeStatusId}>
                {submitting ? "Import en cours…" : (
                  <>
                    <Check className="size-4" />
                    Importer {mappedRows.length} ligne{mappedRows.length > 1 ? "s" : ""}
                  </>
                )}
              </Button>
            )}
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
